"use client";

import { Container, Title, Text, Stack, SimpleGrid, Paper, Group, Badge, Button, Box } from "@mantine/core";
import { Link } from "@/i18n/navigation";
import { IconCamera, IconSparkles, IconBrain } from "@tabler/icons-react";
import { trackCtaClick } from "@/lib/analytics";

interface UnifiedUpsellProps {
  title?: string;
  subtitle?: string;
  showVirtualDraping?: boolean;
  showAIAnalysis?: boolean;
  showQuiz?: boolean;
  className?: string;
}

export default function UnifiedUpsell({
  title = "Not sure what your color season is?",
  subtitle,
  showVirtualDraping = true,
  showAIAnalysis = true,
  showQuiz = true,
  className,
}: UnifiedUpsellProps) {
  return (
    <Container size="lg" py="xl" className={className}>
      <Paper
        p="xl"
        radius="lg"
        withBorder
        style={{
          background: "linear-gradient(to bottom, var(--mantine-color-wumpus-0), var(--mantine-color-wumpus-1))",
        }}
      >
        <Stack gap="xl">
          <Title order={2} ta="center" size="h1" fw={700}>
            {title}
          </Title>
          {subtitle && (
            <Text ta="center" size="lg" c="dimmed">
              {subtitle}
            </Text>
          )}

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={{ base: "md", md: "xl" }}>
            {showVirtualDraping && (
              <Paper
                p="xl"
                radius="md"
                withBorder
                style={{
                  background: "var(--mantine-color-white)",
                  borderColor: "var(--mantine-color-blue-2)",
                }}
              >
                <Stack gap="md" align="center">
                  <Box
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: "var(--mantine-color-blue-1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconCamera size={32} color="var(--mantine-color-blue-6)" />
                  </Box>
                  <Title order={3} ta="center" fw={600}>
                    Virtual Draping Tool
                  </Title>
                  <Group justify="center" gap="xs">
                    <Badge color="blue" variant="light">
                      Free
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" ta="center">
                    A fun, quick way to preview yourself in all 12 color palettes
                  </Text>
                  <Button
                    variant="light"
                    component={Link}
                    color="blue"
                    size="lg"
                    fullWidth
                    leftSection={<IconBrain size={20} />}
                    href="/virtual-color-draping"
                    mt="md"
                  >
                    Try Virtual Draping
                  </Button>
                </Stack>
              </Paper>
            )}

            {showAIAnalysis && (
              <Paper
                p="xl"
                radius="md"
                withBorder
                style={{
                  background: "var(--mantine-color-white)",
                  borderColor: "var(--mantine-color-wumpus-4)",
                  borderWidth: "2px",
                }}
              >
                <Stack gap="md" align="center">
                  <Box
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: "var(--mantine-color-violet-1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconSparkles size={32} color="var(--mantine-color-violet-6)" />
                  </Box>
                  <Title order={3} ta="center" fw={600}>
                    Professional Color Analysis
                  </Title>
                  <Group justify="center" gap="xs">
                    <Badge color="violet" variant="light">
                      Premium
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" ta="center">
                    Get personalized results based on your photos
                  </Text>
                  <Button
                    variant="filled"
                    component={Link}
                    href="/ai-color-analysis"
                    onClick={() => trackCtaClick("unified_upsell_ai")}
                    color="violet"
                    size="lg"
                    fullWidth
                    leftSection={<IconSparkles size={20} />}
                    mt="md"
                  >
                    Get Your Analysis
                  </Button>
                </Stack>
              </Paper>
            )}

            {showQuiz && (
              <Paper
                p="xl"
                radius="md"
                withBorder
                style={{
                  background: "var(--mantine-color-white)",
                  borderColor: "var(--mantine-color-blue-2)",
                }}
              >
                <Stack gap="md" align="center">
                  <Box
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: "var(--mantine-color-blue-1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconBrain size={32} color="var(--mantine-color-blue-6)" />
                  </Box>
                  <Title order={3} ta="center" fw={600}>
                    Color Analysis Quiz
                  </Title>
                  <Group justify="center" gap="xs">
                    <Badge color="blue" variant="light">
                      Free
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" ta="center">
                    A fun, quick way to get started with color analysis
                  </Text>
                  <Button
                    variant="light"
                    component={Link}
                    color="blue"
                    size="lg"
                    fullWidth
                    leftSection={<IconBrain size={20} />}
                    href="/color-analysis-quiz"
                    mt="md"
                  >
                    Take the Quiz
                  </Button>
                </Stack>
              </Paper>
            )}
          </SimpleGrid>
        </Stack>
      </Paper>
    </Container>
  );
}
